/**
 * The registry that turns stored identifiers back into the names people use.
 *
 * Every table in this system references its neighbours by ULID, which is right for storage and
 * useless on screen: a compensation row that says `01JGX7…` instead of "McKenzie Taylor" is a
 * record only a database can read. Rather than teach each page how to resolve its own foreign
 * keys — fifteen slightly different implementations, drifting apart from the day they are
 * written — every referenceable collection is declared once, here.
 *
 * Adding a new referenceable entity should be a single entry in this file, and nothing else.
 */

export interface EntityResource {
  /** List endpoint, which must exist in the OpenAPI contract. */
  listPath: string;
  /** The name a person would use for this row. */
  label: (row: Record<string, unknown>) => string;
  /** Defaults to `row.id`; override where the list is nested or the key differs. */
  value?: (row: Record<string, unknown>) => string;
  /** Pull the rows out of a non-flat payload. */
  extract?: (data: unknown) => Record<string, unknown>[];
  /** Secondary line in a picker, when the name alone is not enough to choose confidently. */
  hint?: (row: Record<string, unknown>) => string | undefined;
  /**
   * Heading this row sits under in a picker, and the qualifier printed in front of its name
   * everywhere else.
   *
   * For collections where a name is only unique inside its parent. There is a shelf in the bar, a
   * shelf in the kitchen and a shelf in the bodega; a dozen entries all reading "Shelf" in one flat
   * list, distinguished only by whoever remembered to type the room into the name, is the same
   * problem the owner already rejected on the floor plan.
   */
  group?: (row: Record<string, unknown>) => string | undefined;
  /** Singular noun for empty and error copy: "No suppliers yet". */
  noun: string;
}

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));

/** Rows out of a flat list payload, for the entries that filter one endpoint down to a subset. */
const rowsOf = (data: unknown): Record<string, unknown>[] =>
  Array.isArray(data) ? (data as Record<string, unknown>[]) : [];

export const ENTITIES = {
  staff: {
    listPath: '/admin/v1/staff/members',
    label: (r) => str(r.displayName) || str(r.fullName) || 'Unnamed',
    hint: (r) => (r.status === 'terminated' ? 'No longer employed' : undefined),
    noun: 'staff member',
  },
  category: {
    listPath: '/admin/v1/catalog/categories',
    label: (r) => str(r.nameEn),
    hint: (r) => (r.nameEs ? str(r.nameEs) : undefined),
    noun: 'category',
  },
  // `/tax/groups` returns `{ group, components }` per row rather than a flat record — the reason
  // `value` and `extract` exist on this interface at all.
  taxGroup: {
    listPath: '/admin/v1/tax/groups',
    label: (r) => str((r.group as Record<string, unknown> | undefined)?.name),
    value: (r) => str((r.group as Record<string, unknown> | undefined)?.id),
    noun: 'tax group',
  },
  /*
   * THE MENU'S LISTS. `scope=menu` leaves out the lists that belong to one named regular, because
   * every picker built on this entry asks "which menu?" — which price list does this channel sell
   * from, which one am I editing prices on — and somebody's negotiated arrangement is never the
   * answer. It is not hidden: what one person pays is read and changed on that person's own screen,
   * in items and sentences rather than lists and priorities.
   */
  priceList: {
    listPath: '/admin/v1/pricing/price-lists?scope=menu',
    label: (r) => str(r.name),
    noun: 'price list',
  },
  channel: {
    listPath: '/admin/v1/channels/channels',
    label: (r) => str(r.name),
    noun: 'channel',
  },
  // `/floor/zones` returns `{ zone, spots }` per row, like `/tax/groups` and `/referrals/plans`.
  // Reading `name`/`id` off the wrapper gives a blank label and an empty value, which makes the
  // required Area field on the new-place form impossible to complete.
  zone: {
    listPath: '/admin/v1/floor/zones',
    label: (r) => str((r.zone as Record<string, unknown> | undefined)?.name),
    value: (r) => str((r.zone as Record<string, unknown> | undefined)?.id),
    noun: 'area',
  },
  /*
   * THREE ENTRIES OVER ONE ENDPOINT, because "supplier" means two things here and every picker has
   * to say which. Carriers are vendors too — the ferry and the freight forwarders that bring goods
   * over from the mainland — so they live in the same table (`docs/domain/shipping.md`), and each
   * of these three filters it to the vendors the question is actually about.
   *
   * `both` is the reason `kind` has three values rather than a boolean: an island forwarder that
   * also sells dry goods is one vendor and appears under BOTH filters.
   */

  /** Everyone billed by, whatever they sell — a payables list, a payment, a bill. */
  supplier: {
    listPath: '/admin/v1/suppliers/suppliers',
    label: (r) => str(r.name),
    noun: 'supplier',
  },
  /** Who brought it over. A goods-only vendor here records shipping against the wrong bill. */
  carrier: {
    listPath: '/admin/v1/suppliers/suppliers',
    label: (r) => str(r.name),
    extract: (data) => rowsOf(data).filter((r) => r.kind === 'freight' || r.kind === 'both'),
    noun: 'carrier',
  },
  /**
   * Who it was bought FROM. The filter above ran one way only for as long as it existed, so the
   * order form offered the ferry and the freight forwarders as people to buy limes from — and an
   * order placed with one showed on their page as goods on order and on the vendor comparison as a
   * price to beat. `POST /purchase-orders`, `/receipts` and `/supplier-catalog-items` refuse it now;
   * this is the screen keeping the same rule, so nobody meets that refusal by surprise.
   */
  goodsSupplier: {
    listPath: '/admin/v1/suppliers/suppliers',
    label: (r) => str(r.name),
    extract: (data) => rowsOf(data).filter((r) => r.kind === 'goods' || r.kind === 'both'),
    noun: 'supplier',
  },
  device: {
    listPath: '/admin/v1/operations/devices',
    label: (r) => str(r.name),
    noun: 'device',
  },
  /**
   * The physical box the money is in — a cash drawer, the float box, the safe.
   *
   * Only drawers are offered, because the one field that picks one is "which drawer does this register
   * use", and a register pointed at the safe would count the whole business off against a bartender's
   * shift. A retired box stays in the list — a register that still names it has to read back as a name
   * rather than as "Not found" — and the hint is what stops it being picked again by mistake.
   */
  cashBox: {
    listPath: '/admin/v1/cash/locations',
    label: (r) => str(r.name),
    extract: (data) => rowsOf(data).filter((r) => r.kind === 'drawer'),
    hint: (r) => (r.active === false ? 'No longer in use' : undefined),
    noun: 'cash drawer',
  },
  referralProfile: {
    listPath: '/admin/v1/referrals/profiles',
    label: (r) => str(r.name),
    // A driver who has left the island stays on file so his old commission still reads back as a
    // name, but he is offered in "the agency they drive for" looking exactly like somebody still
    // working — and a new driver put under him earns nobody anything.
    hint: (r) => (r.active === false ? 'No longer working with you' : undefined),
    noun: 'referral partner',
  },
  inventoryItem: {
    listPath: '/admin/v1/inventory/items',
    label: (r) => str(r.nameEn),
    // Retired items stay in the list so that a movement recorded against one still reads back as a
    // name rather than "Not found"; the hint is what keeps them from being picked again by mistake.
    hint: (r) =>
      r.active === false ? 'No longer stocked' : r.stockUnit ? `per ${str(r.stockUnit)}` : undefined,
    noun: 'stock item',
  },
  // The rooms stock is kept in. Everything below is somewhere INSIDE one of these.
  storageRoom: {
    listPath: '/admin/v1/inventory/storage-rooms',
    label: (r) => str(r.name),
    hint: (r) => (r.active === false ? 'No longer in use' : undefined),
    noun: 'room',
  },
  // Stock is held per area, never per site, so every movement — waste, a transfer, a count — has
  // to name the area it happened in before it can be recorded at all.
  //
  // Grouped by room, and the room is the endpoint's own answer rather than a prefix somebody typed
  // into the name. A flat list of a dozen areas where "the bar one" is whichever of them starts
  // with "Bar" is a naming convention pretending to be structure, and it fails the first time
  // somebody adds "Back cooler" without the prefix.
  //
  // The room is in the LABEL as well, which the grouping alone does not cover: there is a shelf in
  // all three rooms and a deep freezer in all three, so a heading answers the question only while
  // the list is open. Everywhere else — the closed picker, a table cell, the line in a confirmation
  // — one of three identical "Shelf"s was shown with nothing to say which, and on the field that
  // decides which shelf a pour comes off, picking the wrong one sends every drink to the bodega.
  // Same "Parent — Child" shape `variant` and `modifierOption` use for the same reason. Derived
  // from the room rather than stored in the name, so it cannot drift out of step with it.
  storageLocation: {
    listPath: '/admin/v1/inventory/storage-locations',
    label: (r) => (r.roomName ? `${str(r.roomName)} — ${str(r.name)}` : str(r.name)),
    hint: (r) => (r.active === false ? 'No longer in use' : undefined),
    noun: 'storage area',
  },
  // A recipe can be an ingredient of another one — the house grind that goes into the patty that
  // goes into the burger. The hint says what a batch of it makes, because that is the unit the
  // recipe using it has to be written in.
  recipe: {
    listPath: '/admin/v1/inventory/recipes',
    label: (r) => str(r.name),
    hint: (r) => (r.yieldUnit ? `made in ${str(r.yieldUnit)}` : undefined),
    noun: 'recipe',
  },
  /**
   * Reason codes are picked by their `code`, not their id: the code is what the stock ledger and
   * the fiscal document store, and what the offline register will send for the same event.
   *
   * Each kind is a separate entry rather than one `reasonCode` resource because the taxonomy is
   * only useful if the list is short and every line on it is plausible — offering "Cash paid out
   * of the drawer" to someone logging a dropped bottle is how a manager learns to pick the first
   * option every time.
   */
  wasteReason: {
    listPath: '/admin/v1/admin/reason-codes',
    label: (r) => str(r.nameEn),
    value: (r) => str(r.code),
    extract: (data) => rowsOf(data).filter((r) => r.kind === 'waste' && r.active !== false),
    noun: 'reason',
  },
  creditNoteReason: {
    listPath: '/admin/v1/admin/reason-codes',
    label: (r) => str(r.nameEn),
    value: (r) => str(r.code),
    extract: (data) =>
      rowsOf(data).filter((r) => (r.kind === 'refund' || r.kind === 'correction') && r.active !== false),
    noun: 'reason',
  },
  // A credit note must be numbered from a CAI range registered for credit notes; printing one out
  // of the invoice range is the mistake Honduran fiscal rules exist to prevent, so the picker can
  // only ever offer ranges that are allowed to carry it.
  creditNoteRange: {
    listPath: '/admin/v1/fiscal/emission-points',
    label: (r) => `Register ${str(r.emissionPointCode)}`,
    extract: (data) => rowsOf(data).filter((r) => r.documentType === 'creditNote' && r.active !== false),
    hint: (r) => (r.caiCode ? `CAI ${str(r.caiCode)}` : undefined),
    noun: 'credit note range',
  },
  menuItem: {
    listPath: '/admin/v1/catalog/items',
    label: (r) => str(r.nameEn),
    noun: 'menu item',
  },
  // `/referrals/plans` returns `{ plan, tiers }` per row, like `/tax/groups`.
  commissionPlan: {
    listPath: '/admin/v1/referrals/plans',
    label: (r) => str((r.plan as Record<string, unknown> | undefined)?.name),
    value: (r) => str((r.plan as Record<string, unknown> | undefined)?.id),
    noun: 'commission plan',
  },
  /**
   * A size is only choosable once it says what it is a size OF: a dozen drinks have a "Bottle", and
   * the column that says which drink holds a ULID. Same for a choice and its set.
   *
   * Both are also the reason the price screen used to ask for a pasted identifier — there was no
   * flat list to build a picker from, so the field could only be filled from a database console.
   */
  variant: {
    listPath: '/admin/v1/catalog/variants',
    label: (r) => `${str(r.itemName)} — ${str(r.nameEn)}`,
    hint: (r) => (r.active === false ? 'Not on sale' : undefined),
    noun: 'size',
  },
  modifierOption: {
    listPath: '/admin/v1/catalog/modifier-options',
    label: (r) => `${str(r.groupName)} — ${str(r.nameEn)}`,
    hint: (r) => (r.active === false ? 'Not offered' : undefined),
    noun: 'choice',
  },
  bundle: {
    listPath: '/admin/v1/catalog/bundles',
    label: (r) => str(r.nameEn),
    hint: (r) => (r.active === false ? 'Not on sale' : undefined),
    noun: 'combo',
  },
} as const satisfies Record<string, EntityResource>;

export type EntityKey = keyof typeof ENTITIES;

// `satisfies` above keeps each entry checked against EntityResource while preserving the literal
// keys for EntityKey. Reading back through the interface is what restores access to the optional
// members, which the narrowed literal types would otherwise hide.
const REGISTRY = ENTITIES as Record<EntityKey, EntityResource>;

export function entityResource(key: EntityKey): EntityResource {
  return REGISTRY[key];
}

/** Rows out of a list payload, honouring a resource's custom extractor. */
export function entityRows(key: EntityKey, data: unknown): Record<string, unknown>[] {
  const resource = REGISTRY[key];
  if (resource.extract) return resource.extract(data);
  return Array.isArray(data) ? (data as Record<string, unknown>[]) : [];
}

export function entityValue(key: EntityKey, row: Record<string, unknown>): string {
  const resource = REGISTRY[key];
  return resource.value ? resource.value(row) : str(row.id);
}
